type Item = {
  alt?: string
  provider?: string
  src: string
}

type MediaType = 'image' | 'video' | 'unknown' // | '360' | 'youtube' | 'vimeo'

type MediaItem = Item & {
  index: number
  type: MediaType
}

type ImageOptions = {
  height?: number
  quality?: number
  stripmetadata?: number
  stripcolorprofile?: number
  width?: number
}

type BaseGalleryOptions = {
  dots?: boolean
  navigation?: boolean
  thumbs?: boolean
  thumbsDirection?: 'horizontal' | 'vertical'
  thumbsTrigger?: 'click' | 'mouseover'
  variant?: string
  XofY?: boolean
  zoom?: boolean
}

export type GalleryOptions = BaseGalleryOptions & {
  fullscreen?: boolean
  fullscreenOptions?: BaseGalleryOptions
  index?: number
  media?: Item[]
  showAll?: boolean
}

export function useUiMediaGallery(name: string, options: GalleryOptions | null = null) {
  const defaultOptions: GalleryOptions = {
    dots: true,
    fullscreen: true,
    fullscreenOptions: {
      dots: true,
      navigation: true,
      thumbs: true,
      thumbsDirection: 'vertical',
      thumbsTrigger: 'click',
      XofY: true,
    },
    index: 0,
    navigation: true,
    thumbsDirection: 'horizontal',
    thumbsTrigger: 'click',
    thumbs: true,
    XofY: true,
    showAll: true,
  }

  const galleryOptions = useState<GalleryOptions>(`${name}__gallery-options`, () => ({
    ...defaultOptions,
  }))

  if (options) {
    galleryOptions.value = {
      ...defaultOptions,
      ...options,
      fullscreenOptions: {
        ...defaultOptions.fullscreenOptions,
        ...(options?.fullscreenOptions || {}),
      },
    }
  }

  const galleryItems = useState<MediaItem[]>(`${name}__gallery-items`, () => [])
  const activeIndex = useState<number>(`${name}__gallery-active-index`, () => galleryOptions.value.index || 0)
  const scrollBehavior = useState<'smooth' | 'instant' | 'auto'>(`${name}__gallery-scroll-behavior`, () => 'smooth')

  const MIN_ZOOM = 1
  const MAX_ZOOM = 3
  const ZOOM_STEP = 0.5

  const zoomLevel = useState<number>(`${name}__gallery-zoom-level`, () => MIN_ZOOM)
  const zoomPan = useState<{ x: number, y: number }>(`${name}__gallery-zoom-pan`, () => ({ x: 0, y: 0 }))
  const enlarged = computed(() => zoomLevel.value > MIN_ZOOM)

  const activeItem = computed(() => {
    return galleryItems.value.find((item: MediaItem) => item?.index === activeIndex.value) || null
  })

  function getImageFromProvider(item: MediaItem, options?: ImageOptions): string {
    if (!item?.src) {
      return ''
    }

    const startParam = item.src.includes('?') ? '&' : '?'
    const params: string[] = []
    switch (item.provider) {
      case 'default':
      case 'xcdn':

        if (options?.width && options?.height) {
          params.push(`f=rs:fit:${options.width}:${options.height}:0:0`)
        }
        else if (options?.width) {
          params.push(`f=rs:fit:${options.width}:0:0:0`)
        }
        else if (options?.height) {
          params.push(`f=rs:fit:0:${options.height}:0:0`)
        }

        params.push(`q:${options?.quality || 100}`)
        params.push(`sm:${options?.stripmetadata || 1}`)
        params.push(`scp:${options?.stripcolorprofile || 1}`)

        return `${item.src}${params.length ? startParam + params.join('/') : ''}`
      case 'scayle':
        if (options?.width) {
          params.push(`width=${options.width}`)
        }

        if (options?.height) {
          params.push(`heigth=${options.height}`)
        }

        if (options?.quality) {
          params.push(`quality=${options.quality}`)
        }
        return `${item.src}${params.length ? startParam + params.join('&') : ''}`
      case 'contentful':
        params.push('f=faces')
        params.push('fit=fill')

        if (options?.width) {
          params.push(`w=${options.width}`)
        }

        if (options?.height) {
          params.push(`h=${options.height}`)
        }

        return `${item.src}${params.length ? startParam + params.join('&') : ''}`
      default:
        return item.src
    }
  }

  if (options?.media?.length) {
    galleryItems.value = options?.media.map((item: Item, index: number): MediaItem => {
      const types: Record<string, string> = {
        '.jpg': 'image',
        '.jpeg': 'image',
        '.png': 'image',
        '.webp': 'image',
        '.avif': 'image',
        '.gif': 'image',

        '.mp4': 'video',
        'youtu.be': 'video',
        'youtube': 'video',
        'vimeo': 'video',
      }

      const typeKey: string
        = Object.keys(types).find(type => item.src?.includes(type)) || ''

      const type: MediaType = typeKey ? types[typeKey] as MediaType : 'unknown'

      return {
        ...item,
        type,
        index: index,
        provider: item.provider || 'default',
      }
    })

    activeIndex.value = galleryOptions.value.index || 0
  }

  function setZoom(level: number) {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, level))

    if (clamped === MIN_ZOOM) {
      zoomPan.value = { x: 0, y: 0 }
    }

    zoomLevel.value = clamped
  }

  const zoomIn = () => {
    setZoom(zoomLevel.value + ZOOM_STEP)
  }
  const zoomOut = () => {
    setZoom(zoomLevel.value - ZOOM_STEP)
  }
  const zoomReset = () => {
    setZoom(MIN_ZOOM)
  }

  return {
    galleryItems,
    galleryOptions,
    activeIndex,
    activeItem,
    getImageFromProvider,
    scrollBehavior,
    enlarged,
    zoomLevel,
    zoomPan,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    zoomStep: ZOOM_STEP,
    setZoom,
    zoomIn,
    zoomOut,
    zoomReset,
  }
}

export type { Item, MediaItem, ImageOptions }
